import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { ArrowLeft, Loader2, Phone, Mail, Lock, CheckCircle, KeyRound, Eye, EyeOff, HelpCircle, UserPlus, AlertCircle } from 'lucide-react';

/**
 * Forgot-password flow supports two methods:
 *   - Phone (OTP via MSG91)
 *   - Email (time-limited reset link via Gmail SMTP)
 * Error state is actionable: links to email flow, sign-up, and support.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [method, setMethod] = useState('phone'); // 'phone' | 'email'
  const [step, setStep] = useState('input'); // input | otp | reset | emailSent | done

  // Phone flow state
  const [phone, setPhone] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Email flow state
  const [email, setEmail] = useState('');

  // Shared password-set state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOtp = async () => {
    if (!phone.trim()) { setError('Please enter your phone number'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/forgot-password/send-otp', { phone: phone.trim() });
      setOtpToken(data.otp_token);
      if (data._dev_otp) setDevOtp(data._dev_otp);
      setStep('otp');
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleSendEmailLink = async () => {
    if (!email.trim() || !email.includes('@')) { setError('Please enter a valid email'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/forgot-password/send-email-link', { email: email.trim().toLowerCase() });
      setStep('emailSent');
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { setError('Please enter the OTP'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/forgot-password/verify-otp', { otp_token: otpToken, code: otpCode.trim() });
      setResetToken(data.reset_token);
      setStep('reset');
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain at least 1 uppercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain at least 1 number'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/forgot-password/reset', { reset_token: resetToken, new_password: newPassword });
      setStep('done');
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const isNoAccountError = error && /no account|not found/i.test(error);

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md px-6 py-10">
        <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login">
          <ArrowLeft size={16} /> Back to Login
        </Link>

        {step === 'done' ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="reset-success">
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Password Reset Successful</h2>
            <p className="text-sm text-slate-600 mb-6">Your password has been updated. You can now log in with your new password.</p>
            <button onClick={() => navigate('/login')} className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700" data-testid="go-to-login">
              Go to Login
            </button>
          </div>
        ) : step === 'emailSent' ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="email-sent">
            <Mail size={40} className="text-sky-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Check Your Email</h2>
            <p className="text-sm text-slate-600 mb-2">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
            <p className="text-xs text-slate-500 mb-6">Didn't get it? Check spam. Link expires in 30 minutes.</p>
            <button onClick={() => { setStep('input'); setMethod('email'); }} className="w-full py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm hover:border-sky-300" data-testid="resend-email-btn">
              Try a different email
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step === 'input' && 'Reset Your Password'}
                {step === 'otp' && 'Verify Your Phone'}
                {step === 'reset' && 'Set New Password'}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {step === 'input' && method === 'phone' && 'Enter the phone number linked to your account.'}
                {step === 'input' && method === 'email' && 'Enter your account email to receive a reset link.'}
                {step === 'otp' && 'Enter the OTP sent to your phone.'}
                {step === 'reset' && 'Create a strong new password.'}
              </p>
            </div>

            {/* Method toggle — only on input step */}
            {step === 'input' && (
              <div className="flex gap-2 p-1 bg-slate-100 rounded-full">
                <button onClick={() => { setMethod('phone'); setError(''); }}
                  className={`flex-1 py-2 rounded-full text-xs font-semibold transition-all ${method === 'phone' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="method-phone-btn">
                  <Phone size={12} className="inline mr-1" /> By Phone
                </button>
                <button onClick={() => { setMethod('email'); setError(''); }}
                  className={`flex-1 py-2 rounded-full text-xs font-semibold transition-all ${method === 'email' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="method-email-btn">
                  <Mail size={12} className="inline mr-1" /> By Email
                </button>
              </div>
            )}

            {step === 'input' && method === 'phone' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Phone Number</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="tel" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g., 9876543210" data-testid="forgot-phone-input" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Enter with or without +91; digits only is fine.</p>
                </div>
                <button onClick={handleSendOtp} disabled={loading}
                  className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-otp-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
            )}

            {step === 'input' && method === 'email' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email Address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="email" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email-input" />
                  </div>
                </div>
                <button onClick={handleSendEmailLink} disabled={loading}
                  className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-email-link-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            )}

            {step === 'otp' && (
              <div className="space-y-4">
                {devOtp && (
                  <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700 text-center">
                    Dev mode — OTP: <strong className="font-mono">{devOtp}</strong>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Enter OTP</label>
                  <input type="text" maxLength={4}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm text-center font-mono tracking-[0.5em] outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="----" data-testid="forgot-otp-input" />
                </div>
                <button onClick={handleVerifyOtp} disabled={loading}
                  className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="verify-otp-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            )}

            {step === 'reset' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">New Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type={showNewPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Confirm Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" data-testid="confirm-password-input" />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button onClick={handleResetPassword} disabled={loading}
                  className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-password-btn">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            )}

            {/* Error + actionable next-steps */}
            {error && !isNoAccountError && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-testid="forgot-error">
                <AlertCircle size={14} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-rose-700">{error}</p>
              </div>
            )}
            {isNoAccountError && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-3" data-testid="forgot-no-account">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="text-rose-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-700 font-medium">{error}</p>
                </div>
                <p className="text-xs text-slate-600 pl-6">You might have registered with a different {method}. Try one of these:</p>
                <div className="space-y-1.5 pl-6">
                  {method === 'phone' && (
                    <button onClick={() => { setMethod('email'); setError(''); }}
                      className="flex items-center gap-2 text-xs text-sky-700 hover:text-sky-800 font-medium" data-testid="try-email-instead">
                      <Mail size={12} /> Try with email instead →
                    </button>
                  )}
                  {method === 'email' && (
                    <button onClick={() => { setMethod('phone'); setError(''); }}
                      className="flex items-center gap-2 text-xs text-sky-700 hover:text-sky-800 font-medium" data-testid="try-phone-instead">
                      <Phone size={12} /> Try with phone instead →
                    </button>
                  )}
                  <Link to="/register" className="flex items-center gap-2 text-xs text-sky-700 hover:text-sky-800 font-medium" data-testid="signup-link">
                    <UserPlus size={12} /> New to DocSite? Sign up →
                  </Link>
                  <a href="mailto:contact@docsite.care?subject=Password%20reset%20help" className="flex items-center gap-2 text-xs text-sky-700 hover:text-sky-800 font-medium" data-testid="contact-support">
                    <HelpCircle size={12} /> Need help? contact@docsite.care
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
